import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import type { Club } from "@/api/clubsApi";

interface ClubActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  club: Club;
  userRole: "owner" | "cohost" | "member" | null;
  isMember: boolean;
  onPostClick: () => void;
  onHangClick: () => void;
  onGatherClick: () => void;
}

/**
 * ClubActionModal - Web equivalent of mobile's ClubActionBottomSheet.
 * Shows available actions based on user permissions.
 */
export function ClubActionModal({
  open,
  onOpenChange,
  club,
  userRole,
  isMember,
  onPostClick,
  onHangClick,
  onGatherClick,
}: ClubActionModalProps) {
  const isOwnerOrCohost = userRole === "owner" || userRole === "cohost";

  // Permission logic matching mobile
  const canCreatePost =
    isMember && (isOwnerOrCohost || club.post_permission === "everyone");
  const canCreateHang =
    isMember && (isOwnerOrCohost || club.hang_permission === "everyone");
  const canCreateEvent = isMember && isOwnerOrCohost;

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Create" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          {canCreatePost && (
            <ActionItem
              icon="memo"
              iconBgColor="bg-surface-2"
              iconColor="text-text-secondary"
              title="Post"
              description="Share something with the club"
              onClick={onPostClick}
            />
          )}

          {canCreateHang && (
            <ActionItem
              icon="bolt"
              iconBgColor="bg-yellow"
              iconColor="text-deep-slate"
              title="Hang"
              description="Let others know where you'll be"
              onClick={onHangClick}
            />
          )}

          {canCreateEvent && (
            <ActionItem
              icon="calendar-plus"
              iconBgColor="bg-blue"
              iconColor="text-off-white"
              title="Create event"
              description="Schedule a time to get together"
              onClick={onGatherClick}
            />
          )}
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}

interface ActionItemProps {
  icon: string;
  iconBgColor: string;
  iconColor: string;
  title: string;
  description: string;
  onClick: () => void;
}

function ActionItem({
  icon,
  iconBgColor,
  iconColor,
  title,
  description,
  onClick,
}: ActionItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 pl-4 pr-6 py-4 rounded-full border border-border-tertiary hover:bg-surface transition-colors text-left focus:outline-none"
    >
      <div
        className={`w-12 h-12 rounded-full ${iconBgColor} flex items-center justify-center flex-shrink-0`}
      >
        <Icon name={icon} size={20} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-body-1-medium text-text-primary">{title}</p>
        <p className="text-body-1 text-text-secondary">{description}</p>
      </div>
    </button>
  );
}
